import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  type AssetsAssetCreateSchema,
  type AssetsAssetSchemaTypedWithId,
  type AssetsSegmentSchemaWithId,
  httpPostAssets,
  httpPostCollectionsByCollectionIdContents,
} from '@iconik-io/iconik-api/src/services/assets';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import { normalizeEntities } from '@iconik-io/shared-lib/src/redux/entities/utils';
import { getProxyMediaType } from '@iconik-io/shared-lib/src/utils/get_proxy_media_type';
import { MEDIA_TYPE_LINK } from '@iconik-io/shared-lib/src/utils/media_type';

import type { AppDispatch, GetState } from '../../store/types';
import { segmentTrackEvent } from '../analytics_tracking/thunks';
import { errorMessage, successMessage } from '../messages/thunks';
import { selectAssetProxy } from '../pages/asset/selectors';
import { searchActions } from '../pages/search/actions';

export const createPlaceholder =
  (data: { title: string }, collectionId?: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const body: AssetsAssetCreateSchema = { ...data, type: 'PLACEHOLDER' };
    if (collectionId) {
      body.collection_id = collectionId;
    }
    const response = await dispatch(httpPostAssets(body));

    // No annotation: httpPostAssets returns AssetSchemaTyped, a union whose
    // post member the generic elastic schema cannot hold. This becomes a
    // SearchItemEntity below, which covers every member.
    const newPlaceholder = {
      ...response.body,
      person_ids: response.body.person_ids || undefined,
      media_type: 'placeholder',
      object_type: 'assets',
    };

    if (collectionId && newPlaceholder.id && newPlaceholder.object_type) {
      await dispatch(
        httpPostCollectionsByCollectionIdContents(
          collectionId,
          {
            object_id: newPlaceholder.id,
            object_type: newPlaceholder.object_type,
          },
          { index_immediately: true },
        ),
      );
    }

    if (newPlaceholder.id) {
      const searchItem: SearchItemEntity = {
        ...newPlaceholder,
        id: newPlaceholder.id,
        object_type: OBJECT_TYPE_ASSETS,
      };

      dispatch(
        updateEntities(normalizeEntities(searchItem, 'search_items').entities),
      );
      dispatch(searchActions.prependId(newPlaceholder.id));
    }
  };

export const createLinkAsset =
  (data: { title: string; external_link: string }, collectionId?: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    const body: AssetsAssetCreateSchema = { ...data, type: 'LINK' };

    if (collectionId) {
      body.collection_id = collectionId;
    }

    const response = await dispatch(httpPostAssets(body));

    const newLinkAsset = {
      ...response.body,
      person_ids: response.body.person_ids || undefined,
      media_type: MEDIA_TYPE_LINK,
      object_type: 'assets',
    };

    if (collectionId && newLinkAsset.id && newLinkAsset.object_type) {
      await dispatch(
        httpPostCollectionsByCollectionIdContents(
          collectionId,
          {
            object_id: newLinkAsset.id,
            object_type: newLinkAsset.object_type,
          },
          { index_immediately: true },
        ),
      );
    }

    if (newLinkAsset.id) {
      const searchItem: SearchItemEntity = {
        ...newLinkAsset,
        id: newLinkAsset.id,
        object_type: OBJECT_TYPE_ASSETS,
      };

      dispatch(
        updateEntities(normalizeEntities(searchItem, 'search_items').entities),
      );
      dispatch(searchActions.prependId(newLinkAsset.id));
    }
  };

export const createSubclipAsset =
  (
    title: string,
    segment: AssetsSegmentSchemaWithId,
    collectionId: string | undefined,
    location: 'Modal' | 'Segment Form',
  ) =>
  async (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<AssetsAssetSchemaTypedWithId> =>
    dispatch(
      httpPostAssets(
        {
          title,
          type: 'SUBCLIP',
          original_segment_id: segment.id,
          original_asset_id: segment.asset_id,
          original_version_id: segment.version_id,
          time_start_milliseconds: segment.time_start_milliseconds ?? 0,
          time_end_milliseconds: segment.time_end_milliseconds ?? 0,
          collection_id: collectionId || undefined,
        },
        { assign_to_collection: collectionId !== undefined },
      ),
    )
      .then(({ body: asset }) => {
        const stateSegment = getState().entities.segments[segment.id];
        if (stateSegment) {
          dispatch(
            updateEntities({
              segments: {
                [segment.id]: { ...stateSegment, subclip_id: asset.id },
              },
            }),
          );
        }
        dispatch(successMessage({ text: 'Subclip Created' }));
        dispatch(
          segmentTrackEvent({
            eventName: 'Subclip Created',
            properties: {
              Location: location,
              'Media Type': getProxyMediaType(
                selectAssetProxy(
                  getState(),
                  segment.asset_id,
                  segment.version_id,
                ),
              ),
            },
          }),
        );
        return asset;
      })
      .catch((error) => {
        if (
          error instanceof EndpointError &&
          error.response.status === 403 &&
          error.response.body.errors?.length > 0
        ) {
          const text = error.response.body.errors[0];
          dispatch(
            errorMessage({
              text,
            }),
          );
        }
        throw error;
      });
